import React, { useEffect, useMemo, useState } from "react";
import { sentenceCaseLabel, useTranslation } from "../i18n";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../Components/LoadingSpinner";
import CustomDropdown from "../Components/CustomDropdown";
import { getVendorDashboardAction } from "../actions/vendorDashboard";
import { getVendorProfileView } from "../utils/vendorProfile";

const EMPTY_DASHBOARD_STATE = {
  date: "monthly",
  data: null,
  loading: false,
  error: "",
};
const EMPTY_DASHBOARD_DATA = {};
const DASHBOARD_PERIOD_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const statDefinitions = [
  {
    title: "Total revenue",
    keys: ["totalRevenue", "total_revenue", "revenue"],
    changeKeys: ["totalRevenueChange", "revenueChange"],
    icon: "/assets/totalrevenue.svg",
    color: "gold",
  },
  {
    title: "Active operations",
    keys: [
      "activeOperations",
      "active_operations",
      "activeOrders",
      "active_orders",
    ],
    changeKeys: ["activeOperationsChange", "active_operations_change"],
    icon: "/assets/operations-icon.svg",
    color: "blue",
  },
  {
    title: "Risk alerts",
    keys: ["riskAlerts", "risk_alerts", "riskAlertCount", "risk_alert_count"],
    changeKeys: ["riskAlertsChange", "risk_alerts_change"],
    icon: "/assets/alert-icon.svg",
    color: "red",
  },
  {
    title: "Active shipments",
    keys: [
      "activeShipments",
      "active_shipments",
      "shipmentCount",
      "shipment_count",
    ],
    changeKeys: ["activeShipmentsChange", "active_shipments_change"],
    icon: "/assets/shipment-icon.svg",
    color: "green",
  },
];

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const dashboardContainers = (data) =>
  [
    data?.data?.cards,
    data?.cards,
    data?.data?.statistics,
    data?.statistics,
    data,
    data?.overview,
    data?.summary,
    data?.stats,
    data?.metrics,
    data?.analytics,
    data?.dashboard,
    data?.dashboardData,
    data?.dashboard_data,
    data?.kpis,
    data?.totals,
    data?.counts,
    data?.data,
  ].filter((item) => item && typeof item === "object" && !Array.isArray(item));

const findDashboardValue = (containers, keys) => {
  for (const container of containers) {
    const value = firstValue(...keys.map((key) => container[key]));
    if (value !== undefined) return value;
  }
  return undefined;
};

const displayValue = (value) => {
  if (Array.isArray(value)) return value.length.toLocaleString();
  const resolved =
    value && typeof value === "object"
      ? firstValue(
          value.value,
          value.amount,
          value.count,
          value.total,
          value.current,
        )
      : value;
  if (resolved === undefined) return "—";
  return typeof resolved === "number"
    ? resolved.toLocaleString()
    : String(resolved);
};

const getStats = (data) => {
  const containers = dashboardContainers(data);
  return statDefinitions.map((definition) => {
    const metric = findDashboardValue(containers, definition.keys);
    const nestedChange =
      metric && typeof metric === "object"
        ? firstValue(
            metric.change,
            metric.trend,
            metric.comparison,
            metric.percentageFromLastMonth !== undefined
              ? `${displayValue(metric.percentageFromLastMonth)}%`
              : undefined,
            metric.highPriority !== undefined
              ? `${displayValue(metric.highPriority)} high priority`
              : undefined,
            metric.description,
          )
        : undefined;
    const change = firstValue(
      nestedChange,
      findDashboardValue(containers, definition.changeKeys),
    );
    return {
      ...definition,
      value: displayValue(metric),
      change: change !== undefined ? String(change) : "",
    };
  });
};

const getDashboardArray = (data, keys) => {
  if (Array.isArray(data)) return data;
  for (const container of dashboardContainers(data)) {
    const value = firstValue(...keys.map((key) => container[key]));
    if (Array.isArray(value)) return value;
    const nestedItems = firstValue(
      value?.data,
      value?.items,
      value?.results,
      value?.list,
      value?.records,
    );
    if (Array.isArray(nestedItems)) return nestedItems;
  }
  return [];
};

const displayText = (value) => {
  if (value && typeof value === "object") {
    const resolved = firstValue(
      value.name,
      value.productName,
      value.commodityName,
      value.title,
      value.label,
      value.companyName,
      value.value,
      "",
    );
    return String(resolved);
  }
  return value === undefined || value === null ? "" : String(value);
};

const normalizeOperation = (operation = {}) => {
  const commodity = firstValue(
    operation.commodity,
    operation.product,
    operation.item,
  );
  const buyer = firstValue(
    operation.buyer,
    operation.client,
    operation.customer,
  );
  const product = firstValue(operation.product, operation.item, operation.products?.[0]);
  const status = displayText(
    firstValue(
      operation.status,
      operation.currentStatus,
      operation.orderStatus,
      operation.state,
    ),
  );
  const statusKey = status
    .toLowerCase()
    .replace(/[^a-z]+/g, "-")
    .replace(/^-|-$/g, "");
  const statusClasses = {
    "in-transit": "processing",
    "order-created": "progress-status",
    "order-placed": "pending",
    pending: "pending",
    delayed: "pending",
    completed: "completed",
    delivered: "completed",
    accepted: "completed",
    rejected: "cancelled",
    cancelled: "cancelled",
    canceled: "cancelled",
  };
  return {
    ...operation,
    productName:
      displayText(
        firstValue(
          operation.productName,
          product?.productName,
          product?.name,
          commodity?.productName,
          commodity?.name,
          operation.name,
        ),
      ) || "—",
    sectorName:
      displayText(
        firstValue(
          operation.sectorName,
          operation.sector,
          product?.sectorName,
          product?.sector,
          commodity?.sectorName,
          commodity?.sector,
        ),
      ) || "—",
    commodity:
      displayText(
        firstValue(
          commodity,
          operation.commodityName,
          operation.productName,
          operation.name,
        ),
      ) || "—",
    quantity:
      [
        displayText(
          firstValue(
            operation.quantity,
            operation.volume,
            operation.amount,
            commodity?.quantity,
            product?.quantity,
          ),
        ),
        displayText(
          firstValue(
            operation.quantityUnit,
            operation.unit,
            commodity?.unit,
            product?.unit,
          ),
        ),
        displayText(
          firstValue(operation.sector, commodity?.sector, product?.sector),
        ),
      ]
        .filter(Boolean)
        .join(" · ") || "—",
    buyer:
      displayText(
        firstValue(
          buyer,
          operation.buyerName,
          operation.clientName,
          operation.companyName,
        ),
      ) || "—",
    country:
      displayText(
        firstValue(
          operation.country,
          operation.countryName,
          operation.destinationCountry,
          operation.originCountry,
          buyer?.country,
          buyer?.countryName,
          buyer?.phone,
          buyer?.email,
        ),
      ) || "—",
    orderId: String(
      firstValue(
        operation.orderId,
        operation.operationId,
        operation.reference,
        operation.id,
        operation._id,
        "",
      ),
    ),
    id: String(
      firstValue(
        operation._id,
        operation.id,
        operation.order?._id,
        operation.orderId,
        operation.operationId,
        "",
      ),
    ),
    status: status || "—",
    statusClass: statusClasses[statusKey] || statusKey,
    image: firstValue(
      operation.image,
      operation.imageUrl,
      operation.mainImage,
      operation.productImage,
      product?.mainImage,
      product?.image,
      product?.imageUrl,
      commodity?.mainImage,
      commodity?.image,
      commodity?.imageUrl,
      "",
    ),
  };
};

const getNumericValue = (value) => {
  if (typeof value === "number") return value;
  const parsed = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getCommodities = (data) => {
  const items = getDashboardArray(data, [
    "topCommodities",
    "top_commodities",
    "commodities",
    "products",
  ]);
  const maxValue = Math.max(
    ...items.map((item) =>
      getNumericValue(
        firstValue(item.value, item.amount, item.revenue, item.totalValue),
      ),
    ),
    0,
  );
  const colors = ["black", "orange", "gray", "green", "brown"];
  return items.map((item, index) => {
    const amount = firstValue(
      item.value,
      item.amount,
      item.revenue,
      item.totalValue,
      item.volume,
      item.quantity,
    );
    const reportedPercent = firstValue(
      item.percent,
      item.percentage,
      item.share,
      item.progress,
    );
    let percent =
      reportedPercent === undefined
        ? maxValue
          ? (getNumericValue(amount) / maxValue) * 100
          : 0
        : getNumericValue(reportedPercent);
    if (percent > 0 && percent <= 1) percent *= 100;
    return {
      name:
        displayText(
          firstValue(
            item.name,
            item.commodity,
            item.productName,
            item.product,
            item.title,
          ),
        ) || "—",
      value: displayValue(amount),
      percent: Math.max(0, Math.min(percent, 100)),
      colorClass: colors[index % colors.length],
      image: firstValue(
        item.image,
        item.imageUrl,
        item.mainImage,
        item.productImage,
        "",
      ),
    };
  });
};

const chartSeriesDefinitions = [
  {
    name: "Profile views",
    keys: ["profileViews", "profile_views", "profileViewCount"],
    className: "profile",
  },
  {
    name: "Listing views",
    keys: ["listingViews", "listing_views", "listingViewCount"],
    className: "listing",
  },
  {
    name: "Buyer interest",
    keys: ["buyerInterest", "buyer_interest", "buyerInterests"],
    className: "buyer",
  },
  {
    name: "RFQs received",
    keys: ["rfqsReceived", "rfqs_received", "rfqs", "rfqCount"],
    className: "rfq",
  },
];

const getPerformanceChart = (data) => {
  const containers = dashboardContainers(data);
  const statistics = findDashboardValue(containers, ["statistics"]);
  const reportedStatisticsRecords = Array.isArray(statistics?.data)
    ? statistics.data
    : [];
  const statisticsTotals = statistics?.totals || {};
  const hasStatisticsTotals =
    firstValue(statisticsTotals.created, statisticsTotals.completed) !==
    undefined;
  const statisticsRecords = reportedStatisticsRecords.length
    ? reportedStatisticsRecords
    : hasStatisticsTotals
      ? [
          {
            label: firstValue(statistics.period, "Total"),
            created: statisticsTotals.created,
            completed: statisticsTotals.completed,
          },
        ]
      : [];
  if (statisticsRecords.length) {
    const statisticsSeries = [
      {
        name: "Created",
        className: "profile",
        values: statisticsRecords.map((record) =>
          getNumericValue(record.created),
        ),
      },
      {
        name: "Completed",
        className: "listing",
        values: statisticsRecords.map((record) =>
          getNumericValue(record.completed),
        ),
      },
    ];
    return {
      labels: statisticsRecords.map((record, index) =>
        displayText(firstValue(record.label, record.date, index + 1)),
      ),
      series: statisticsSeries,
    };
  }

  const chart = findDashboardValue(containers, [
    "listingPerformance",
    "listing_performance",
    "performance",
    "chartData",
    "chart_data",
    "performanceData",
  ]);
  let records = Array.isArray(chart) ? chart : [];
  let labels = [];

  if (chart && !Array.isArray(chart) && typeof chart === "object") {
    const chartLabels = firstValue(
      chart.labels,
      chart.categories,
      chart.dates,
      [],
    );
    labels = Array.isArray(chartLabels) ? chartLabels : [];
    const datasets = Array.isArray(chart.series)
      ? chart.series
      : Array.isArray(chart.datasets)
        ? chart.datasets
        : [];
    const getDataset = (definition) =>
      datasets.find((dataset) => {
        const name = String(
          firstValue(dataset?.name, dataset?.key, dataset?.label, ""),
        )
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
        const displayName = definition.name
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
        return (
          name === displayName ||
          definition.keys.some(
            (key) => key.toLowerCase().replace(/[^a-z0-9]/g, "") === name,
          )
        );
      });
    if (!labels.length) {
      const datasetLength = Math.max(
        0,
        ...datasets.map((dataset) => {
          const values = dataset?.data || dataset?.values || [];
          return Array.isArray(values) ? values.length : 0;
        }),
      );
      labels = Array.from({ length: datasetLength }, (_, index) =>
        String(index + 1),
      );
    }
    records = labels.map((label, index) => ({
      label,
      ...Object.fromEntries(
        chartSeriesDefinitions.map((definition) => {
          const dataset = getDataset(definition);
          const datasetValues = dataset?.data || dataset?.values || [];
          return [
            definition.keys[0],
            firstValue(
              ...definition.keys.map((key) => chart[key]?.[index]),
              datasetValues[index],
            ),
          ];
        }),
      ),
    }));
  }

  if (!records.length) {
    const arrays = chartSeriesDefinitions.map((definition) =>
      findDashboardValue(containers, definition.keys),
    );
    const length = Math.max(
      0,
      ...arrays.map((values) => (Array.isArray(values) ? values.length : 0)),
    );
    if (length) {
      const chartLabels = firstValue(
        chart?.labels,
        chart?.categories,
        chart?.dates,
        [],
      );
      labels = Array.isArray(chartLabels) ? chartLabels : [];
      records = Array.from({ length }, (_, index) =>
        Object.fromEntries(
          chartSeriesDefinitions.map((definition, seriesIndex) => [
            definition.keys[0],
            Array.isArray(arrays[seriesIndex])
              ? arrays[seriesIndex][index]
              : undefined,
          ]),
        ),
      );
    }
  }

  if (!records.length) return { labels: [], series: [] };
  const series = chartSeriesDefinitions
    .map((definition) => ({
      ...definition,
      values: records.map((record) =>
        getNumericValue(findDashboardValue([record], definition.keys)),
      ),
    }))
    .filter((item) => item.values.some((value) => value !== 0));
  if (!labels.length)
    labels = records.map((record, index) =>
      displayText(
        firstValue(
          record.label,
          record.date,
          record.period,
          record.month,
          index + 1,
        ),
      ),
    );
  return { labels: labels.map(displayText), series };
};

const getVisibleChartLabels = (labels = []) => {
  const indexes =
    labels.length <= 5
      ? labels.map((_, index) => index)
      : [0, 0.25, 0.5, 0.75, 1].map((factor) =>
          Math.round((labels.length - 1) * factor),
        );

  return indexes.map((index) => ({ index, label: labels[index] }));
};
const getChartPoints = (values, maxValue) =>
  values.map((value, index) => {
    const x = values.length < 2 ? 500 : (index / (values.length - 1)) * 1000;
    const y = maxValue ? 260 - (value / maxValue) * 260 : 260;
    return [x, y];
  });
export default function Dashboard() {
  const { t, text, status: translateStatus } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authMe = useSelector((state) => state.auth?.authMe);
  const vendorProfile = getVendorProfileView(authMe);
  const dashboardState = useSelector(
    (state) => state.vendorDashboard ?? EMPTY_DASHBOARD_STATE,
  );
  const dashboardData = dashboardState.data ?? EMPTY_DASHBOARD_DATA;
  const [operations, setOperations] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [commodityFilter, setCommodityFilter] = useState("");
  const profileCompletionPercentage =
    dashboardData.profileCompletionPercentage ??
    authMe?.profileCompletionPercentage ??
    0;
  const numericProfileCompletionPercentage = Number(profileCompletionPercentage);
  const safeProfileCompletionPercentage = Number.isFinite(numericProfileCompletionPercentage)
    ? Math.max(0, Math.min(100, numericProfileCompletionPercentage))
    : 0;
  const showProfileBanner = profileCompletionPercentage !== 100;
  const [deletePopup, setDeletePopup] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [chartTooltip, setChartTooltip] = useState(null);

  useEffect(() => {
    dispatch(getVendorDashboardAction("monthly"));
  }, [dispatch]);

  const stats = useMemo(() => getStats(dashboardData), [dashboardData]);
  const apiOperations = useMemo(
    () =>
      getDashboardArray(dashboardData, [
        "recentOperations",
        "recent_operations",
        "operations",
        "recentOrders",
        "recent_orders",
        "orders",
        "activeOrders",
        "active_orders",
        "activeOperations",
      ]).map(normalizeOperation),
    [dashboardData],
  );
  const commodities = useMemo(
    () => getCommodities(dashboardData),
    [dashboardData],
  );
  const performanceChart = useMemo(
    () => getPerformanceChart(dashboardData),
    [dashboardData],
  );

  useEffect(() => {
    setOperations(apiOperations);
  }, [apiOperations]);

  const commodityOptions = useMemo(() => {
    return [
      ...new Set(operations.map((item) => item.commodity).filter(Boolean)),
    ];
  }, [operations]);

  const filteredOperations = useMemo(() => {
    return operations.filter((operation) => {
      const searchText = search.trim().toLowerCase();

      const matchesSearch =
        !searchText ||
        operation.commodity?.toLowerCase().includes(searchText) ||
        operation.buyer?.toLowerCase().includes(searchText) ||
        operation.country?.toLowerCase().includes(searchText) ||
        operation.id?.toString().toLowerCase().includes(searchText);

      const matchesStatus = !statusFilter || operation.status === statusFilter;

      const matchesCommodity =
        !commodityFilter || operation.commodity === commodityFilter;

      return matchesSearch && matchesStatus && matchesCommodity;
    });
  }, [operations, search, statusFilter, commodityFilter]);

  const closeDeletePopup = () => {
    setDeletePopup(false);
    setSelectedOperation(null);
  };

  const handleDelete = () => {
    if (!selectedOperation) return;

    setOperations((currentOperations) =>
      currentOperations.filter(
        (operation) => operation.id !== selectedOperation.id,
      ),
    );
    closeDeletePopup();
  };

  const chartMaxValue = Math.max(
    ...performanceChart.series.flatMap((series) => series.values),
    0,
  );
  const onDashboardDateChange = (valueOrEvent) => {
    const date =
      typeof valueOrEvent === "string"
        ? valueOrEvent
        : valueOrEvent?.target?.value;
    setChartTooltip(null);
    if (date) dispatch(getVendorDashboardAction(date));
  };
  return (
    <div className="dashboard-page">
      <section className="dashboard-header">
        <div>
          <h1 className="f-s-28 l-h-32 fw-semibold">{t("ui.welcome.back")}{" "}{vendorProfile.name || t("ui.vendor").toLocaleLowerCase()}
          </h1>
          <p className="dashboard-subtitle f-s-14 l-h-20">{t("ui.manage.your.commodity.listings.respond.to.buyer.inquiries")}{/* <br className="d-none d-md-block" /> */}{t("ui.and.track.your.active.trade.operations")}</p>
        </div>
        <CustomDropdown
          className="performance-period-select"
          ariaLabel={t("ui.filterPerformancePeriod")}
          value={dashboardState.date || "monthly"}
          options={DASHBOARD_PERIOD_OPTIONS}
          onChange={onDashboardDateChange}
        />
      </section>
      {showProfileBanner && (
        <section
          className="dashboard-profile-banner"
          aria-label={text("Profile completion")}
        >
          <div className="dashboard-profile-banner-art" aria-hidden="true">
            <span className="dashboard-profile-banner-orbit dashboard-profile-banner-orbit--one" />
            <span className="dashboard-profile-banner-orbit dashboard-profile-banner-orbit--two" />
            <div className="dashboard-profile-banner-document">
              <div className="dashboard-profile-banner-avatar">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="3.2" fill="currentColor" />
                  <path
                    d="M5.5 19c.5-3.2 2.8-5 6.5-5s6 1.8 6.5 5"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <span className="dashboard-profile-banner-line dashboard-profile-banner-line--long" />
              <span className="dashboard-profile-banner-line" />
              <span className="dashboard-profile-banner-line" />
              <span className="dashboard-profile-banner-check">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="m6 12.5 4 4 8-9"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
            <span className="dashboard-profile-banner-spark" />
            <span className="dashboard-profile-banner-spark dashboard-profile-banner-spark--small" />
          </div>

          <div className="dashboard-profile-banner-content">
            <h2>
              {sentenceCaseLabel(
                text("Complete Your Profile to Start Receiving Orders"),
              )}
            </h2>
            <p>
              {text("Complete the required information to activate your vendor account and access new business opportunities.")}
            </p>
            <div className="dashboard-profile-progress-row">
              <div
                className="dashboard-profile-progress-track"
                role="progressbar"
                aria-label={text("Profile completion")}
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={safeProfileCompletionPercentage}
              >
                <span style={{ width: `${safeProfileCompletionPercentage}%` }} />
              </div>
              <strong>{safeProfileCompletionPercentage}% {text("complete")}</strong>
            </div>
          </div>

          <button
            className="dashboard-profile-banner-cta"
            type="button"
            onClick={() => navigate("/complete-profile")}
          >
            <span>{text("Complete Profile")}</span>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M5 12h13m-5-5 5 5-5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* <button
            className="dashboard-profile-banner-dismiss"
            type="button"
            onClick={() => setShowProfileBanner(false)}
            aria-label={text("Dismiss profile completion banner")}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="m6 6 12 12M18 6 6 18"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button> */}
        </section>
      )}
      {dashboardState.error && (
        <div className="dashboard-request-error" role="alert">
          {text(dashboardState.error)}
        </div>
      )}
      {dashboardState.loading && (
        <LoadingSpinner
          inline
          size="small"
          className="dashboard-refresh-state"
          label={
            dashboardState.data
              ? t("ui.refreshingDashboard")
              : t("ui.loadingDashboard")
          }
        />
      )}
      <section className="row g-3 dashboard-stats">
        {stats.map((item, index) => (
          <div className="col-md-3" key={index}>
            {/* <div className="stat-card">
              <div className={`stat-icon ${item.iconClass}`}>{item.icon}</div>
              <div className="stat-content">
                <p>{item.title}</p>
                <h3>{item.value}</h3>
                <small>
                  <span>↗ {item.growth}</span> vs last month
                </small>
              </div>
            </div> */}
            <div className="overview-stat-card">
              <div className={`overview-stat-icon ${item.color}`}>
                <span
                  className={`overview-stat-icon-img ${item.color}`}
                  style={{
                    "--icon-url": `url(${item.icon})`,
                  }}
                />
              </div>

              <div className="overview-stat-content">
                <span className="overview-stat-title">{text(item.title)}</span>

                <div className="overview-stat-bottom">
                  <strong>{item.value}</strong>
                  <small className={item.color}>{text(item.change)}</small>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>
      <section className="row g-3 dashboard-middle">
        <div className="col-md-12">
          <div className="dashboard-card operations-card modern-table-card">
            <div className="operations-header">
              <div className="operations-title">
                <h2>{t("ui.active.operations")}</h2>
                <p>{t("ui.track.and.manage.your.ongoing.operations")}</p>
              </div>
              <div className="operations-header-actions">
                <div className="table-search">
                  <img src="/assets/search-icon.svg" alt="" />
                  <input
                    type="text"
                    placeholder={t("ui.search.operations")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="search-clear-btn"
                      onClick={() => setSearch("")}
                    >
                      ×
                    </button>
                  )}
                </div>
                <CustomDropdown
                  className="dashboard-operation-filter dashboard-operation-status-filter"
                  ariaLabel={t("ui.filter.operations.by.status")}
                  value={statusFilter}
                  options={[
                    { value: "", label: t("ui.all.status") },
                    // { value: "In Transit", label: t("ui.in.transit") },
                    { value: "Pending", label: t("ui.pending") },
                    { value: "Completed", label: t("ui.completed") },
                    { value: "Delayed", label: t("ui.delayed") },
                  ]}
                  onChange={setStatusFilter}
                />
                <CustomDropdown
                  className="dashboard-operation-filter dashboard-operation-commodity-filter"
                  ariaLabel={t("ui.all.commodity")}
                  value={commodityFilter}
                  options={[
                    { value: "", label: t("ui.all.commodity") },
                    ...commodityOptions.map((commodity) => ({
                      value: commodity,
                      label: commodity,
                    })),
                  ]}
                  onChange={setCommodityFilter}
                />
              </div>
            </div>
            <div className="operations-table-wrapper">
              <table className="operations-table modern-operations-table">
                <thead>
                  <tr>
                    <th>{t("ui.order.id")}</th>
                    <th>{t("ui.product")}</th>
                    <th>{t("ui.client")}</th>
                    <th>{t("ui.status")}</th>
                    <th className="action-heading">{t("ui.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardState.loading && !dashboardState.data ? (
                    <tr>
                      <td colSpan="5">
                        <LoadingSpinner label={t("ui.loadingOperations")} />
                      </td>
                    </tr>
                  ) : filteredOperations.length > 0 ? (
                    filteredOperations.map((operation, index) => (
                      <tr key={operation.id || index}>
                        <td>
                          <span className="operation-id">{operation.orderId}</span>
                        </td>

                        <td>
                          <div className="commodity-cell">
                            {operation.image && (
                              <div className="commodity-img">
                                <img src={operation.image} alt={operation.productName} />
                              </div>
                            )}
                            <div className="commodity-info">
                              <p>{operation.productName}</p>
                              <span>{operation.sectorName}</span>
                            </div>
                          </div>
                        </td>

                        {/* Buyer */}
                        <td>
                          <div className="buyer-cell">
                            <p>{operation.buyer}</p>
                            <span>{operation.country}</span>
                          </div>
                        </td>
                        {/* Status */}
                        <td>
                          <span
                            className={`operation-status ${operation.statusClass || ""}`}
                          >
                            {translateStatus(operation.status)}
                          </span>
                        </td>
                        <td>
                          <div className="table-action-buttons">
                            <button
                              type="button"
                              className="table-icon-btn"
                              title={t("ui.view.operation")}
                              onClick={() => navigate(`/order/${operation.id}`)}
                            >
                              <img src="/assets/eye.svg" alt={t("ui.view")} />
                            </button>
                            {/* <button
                              type="button"
                              className="table-icon-btn"
                              title="Delete operation"
                              onClick={() => openDeletePopup(operation)}
                            >
                              <img src="/assets/trash.svg" alt="Delete" />
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">
                        <div className="inquiries-empty-state">
                          <img src="/assets/search-icon.svg" alt="" />
                          <strong>{t("ui.no.operations.found")}</strong>
                          <span>{t("ui.try.changing.your.search.or.filters")}</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="table-footer">
              <span>{t("ui.showing")}{" "}{filteredOperations.length}{" "}{t("ui.of")}{" "}{operations.length}{" "}{t("ui.operations")}</span>
              {/* <button
                type="button"
                className="view-operations-modern recent-view-all-btn w-25"
                onClick={() => navigate("/order")}
              >
                View all operations
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {deletePopup && selectedOperation && (
        <div className="delete-popup-overlay" onClick={closeDeletePopup}>
          <div
            className="delete-popup-card"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="delete-popup-close"
              onClick={closeDeletePopup}
              aria-label={t("ui.close")}
            >
              ×
            </button>

            <div className="delete-popup-icon">
              <img src="/assets/trash.svg" alt="" />
            </div>

            <div className="delete-popup-content">
              <h3 className="mt-3">{t("ui.delete")}</h3>
              <p>{t("ui.are.you.sure.you.want.to.delete")}{" "}
                <strong>{selectedOperation.id}</strong>{t("ui.this.operation.will.be.permanently.removed.and.this.action.cannot.be.undone")}</p>
            </div>

            <div className="row g-2 delete-popup-actions">
              <div className="col-6">
                <button
                  type="button"
                  className="delete-popup-cancel"
                  onClick={closeDeletePopup}
                >{t("ui.cancel")}</button>
              </div>
              <div className="col-6">
                <button
                  type="button"
                  className="delete-popup-confirm"
                  onClick={handleDelete}
                >{t("ui.delete.operation")}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="row g-3 dashboard-bottom-row">
        <div className="col-md-7">
          <div className="dashboard-card performance-card h-100">
            {/* Header */}
            <div className="performance-header">
              <div>
                <h2>{t("ui.listing.performance")}</h2>
                <p>{t("ui.performance.overview.for.your.listings")}</p>
              </div>

            </div>
            {/* Legend */}
            {performanceChart.series.length > 0 && (
              <div className="modern-chart-legend">
                {performanceChart.series.map((series) => (
                  <span key={series.name}>
                    <i className={`legend-dot ${series.className}`} />
                    {series.name}
                  </span>
                ))}
              </div>
            )}

            {/* Chart */}
            {performanceChart.series.length ? (
              <div className="modern-dashboard-chart">
                <div className="chart-y-axis">
                  {[1, 0.8, 0.6, 0.4, 0.2, 0].map((factor) => (
                    <span key={factor}>
                      {displayValue(Number((chartMaxValue * factor).toFixed(1)))}
                    </span>
                  ))}
                </div>
                <div className="chart-main">
                  <div className="chart-grid">
                    {[0, 1, 2, 3, 4, 5].map((line) => (
                      <span key={line} />
                    ))}
                  </div>
                  <svg
                    className="chart-svg"
                    viewBox="0 0 1000 260"
                    preserveAspectRatio="none"
                  >
                    {performanceChart.series.map((series) => {
                      const points = getChartPoints(
                        series.values,
                        chartMaxValue,
                      );
                      return (
                        <g key={series.name}>
                          <polyline
                            className={`chart-line ${series.className}-line`}
                            points={points
                              .map((point) => point.join(","))
                              .join(" ")}
                          />
                          {points.map(([cx, cy], index) => {
                            const pointLabel = performanceChart.labels[index] || "";
                            const pointValue = series.values[index];
                            const showTooltip = (event) => {
                              const plot = event.currentTarget.ownerSVGElement;
                              const plotBounds = plot.getBoundingClientRect();
                              const chartBounds = plot.parentElement.getBoundingClientRect();
                              const pointTop =
                                plotBounds.top - chartBounds.top +
                                (cy / 260) * plotBounds.height;
                              setChartTooltip({
                                seriesName: series.name,
                                className: series.className,
                                label: pointLabel,
                                value: pointValue,
                                left: Math.max(8, Math.min(92, (cx / 1000) * 100)),
                                top: (pointTop / chartBounds.height) * 100,
                                placement: cy < 60 ? "below" : "above",
                              });
                            };
                            return (
                              <circle
                                key={`${series.name}-${index}`}
                                className={`chart-point ${series.className}-point`}
                                cx={cx}
                                cy={cy}
                                r="4.5"
                                tabIndex="0"
                                aria-label={`${series.name}, ${pointLabel}: ${displayValue(pointValue)}`}
                                onMouseEnter={showTooltip}
                                onMouseLeave={() => setChartTooltip(null)}
                                onFocus={showTooltip}
                                onBlur={() => setChartTooltip(null)}
                              >
                                <title>{`${series.name} · ${pointLabel}: ${displayValue(pointValue)}`}</title>
                              </circle>
                            );
                          })}
                        </g>
                      );
                    })}
                  </svg>
                  {chartTooltip && (
                    <div
                      className={`chart-tooltip ${chartTooltip.placement === "below" ? "chart-tooltip-below" : ""}`}
                      role="tooltip"
                      style={{
                        left: `${chartTooltip.left}%`,
                        top: `${chartTooltip.top}%`,
                      }}
                    >
                      <span className="chart-tooltip-label">{chartTooltip.label}</span>
                      <span className="chart-tooltip-series">
                        <i className={`legend-dot ${chartTooltip.className}`} />
                        {chartTooltip.seriesName}
                        <strong>{displayValue(chartTooltip.value)}</strong>
                      </span>
                    </div>
                  )}
                  <div className="chart-labels">
                    {getVisibleChartLabels(performanceChart.labels).map(({ label, index }) => {
                      const lastIndex = performanceChart.labels.length - 1;
                      const labelClass =
                        index === 0
                          ? "chart-label-start"
                          : index === lastIndex
                            ? "chart-label-end"
                            : "";
                      const position = lastIndex > 0 ? (index / lastIndex) * 100 : 0;
                      return (
                        <span
                          key={`${label}-${index}`}
                          className={labelClass}
                          style={{ left: `${position}%` }}
                        >
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : dashboardState.loading && !dashboardState.data ? (
              <LoadingSpinner label={t("ui.loadingPerformance")} />
            ) : (
              <div className="dashboard-chart-empty">{t("ui.performance.data.is.not.available.for.this.period")}</div>
            )}
            {/* Footer */}
            {/* <div className="performance-footer">
              <button
                type="button"
                className="modern-footer-link"
                onClick={() => navigate("/analytics")}
              >
                View full analytics
                <span>→</span>
              </button>
            </div> */}
          </div>
        </div>
        <div className="col-md-5">
          <div className="dashboard-card modern-commodities-card h-100">
            {/* Header */}
            <div className="commodities-header">
              <div>
                <h2>{t("ui.top.commodities")}</h2>
                <p>{t("ui.top.performing.categories")}</p>
              </div>
            </div>

            {/* Commodities */}
            <div className="modern-commodities-list">
              {commodities.length ? (
                commodities.map((commodity, index) => (
                  <div className="modern-commodity-row" key={index}>
                    <div className="modern-commodity-content">
                      <div
                        className={`modern-commodity-icon ${commodity.colorClass}`}
                      >
                        {commodity.image ? (
                          <img
                            src={commodity.image}
                            alt={`${commodity.name} commodity`}
                          />
                        ) : (
                          <span aria-hidden="true">
                            {commodity.name.charAt(0)}
                          </span>
                        )}
                      </div>

                      <div className="modern-commodity-details">
                        <div className="modern-commodity-top">
                          <strong>{commodity.name}</strong>

                          <span>{commodity.value}</span>
                        </div>

                        <div className="modern-commodity-progress">
                          <div
                            className={`modern-commodity-progress-fill ${commodity.colorClass}`}
                            style={{
                              width: `${commodity.percent}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="dashboard-chart-empty">
                  {dashboardState.loading && !dashboardState.data
                    ? t("ui.loadingCommodities")
                    : t("ui.noCommodityData")}
                </div>
              )}
            </div>

            {/* Footer */}
            {/* <div className="commodities-footer">
              <button
                type="button"
                className="modern-footer-link"
                onClick={() => navigate("/reports")}
              >
                View full report
                <span>→</span>
              </button>
            </div> */}
          </div>
        </div>
      </section>
    </div>
  );
}
